import { useState, useEffect, useRef, useCallback } from 'react';

function safeStorageGet(key) {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageSet(key, value) {
  try {
    localStorage.setItem(key, value);
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function getSpeechSynth() {
  if (typeof window === 'undefined') return null;
  const synth = window.speechSynthesis;
  return synth && typeof synth.speak === 'function' ? synth : null;
}

function createAudioContext() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  return Ctx ? new Ctx() : null;
}

function callKey(call) {
  if (!call) return null;
  return `${call.ticket_number}|${call.counter_name}|${call.call_count ?? 0}|${call.last_called_at || ''}`;
}

function isActivateKey(e) {
  if (e?.key) {
    return e.key === 'Enter' || e.key === ' ' || e.key === 'OK' || e.key === 'Select';
  }
  const code = e?.keyCode ?? e?.which;
  return code === 13 || code === 23 || code === 32;
}

function counterSpoken(call) {
  return call.counter_name || 'the counter';
}

function waitForVoices(synth, timeoutMs = 4000) {
  return new Promise(resolve => {
    const existing = synth.getVoices();
    if (existing.length > 0) return resolve(existing);

    let settled = false;
    const finish = (voices) => {
      if (settled) return;
      settled = true;
      resolve(voices || []);
    };

    const onVoices = () => {
      const voices = synth.getVoices();
      if (voices.length > 0) finish(voices);
    };

    if (typeof synth.addEventListener === 'function') {
      synth.addEventListener('voiceschanged', onVoices, { once: true });
    } else {
      synth.onvoiceschanged = onVoices;
    }

    const deadline = Date.now() + timeoutMs;
    const tick = () => {
      const voices = synth.getVoices();
      if (voices.length > 0) return finish(voices);
      if (Date.now() >= deadline) return finish([]);
      setTimeout(tick, 120);
    };
    setTimeout(tick, 120);
  });
}

function pickVoice(voices) {
  const femaleKeywords = [
    'zira', 'hazel', 'susan', 'female', 'woman',
    'google uk english female', 'samantha', 'karen', 'moira',
    'fiona', 'veena', 'victoria', 'jenny', 'aria',
  ];
  return (
    voices.find(v => femaleKeywords.some(k => v.name.toLowerCase().includes(k))) ||
    voices.find(v => v.lang && v.lang.startsWith('en')) ||
    voices[0] ||
    null
  );
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function waitUntilSpeechIdle(synth, timeoutMs = 20000) {
  return new Promise(resolve => {
    const deadline = Date.now() + timeoutMs;
    const tick = () => {
      if (!synth.speaking && !synth.pending) return resolve(true);
      if (Date.now() >= deadline) return resolve(false);
      setTimeout(tick, 120);
    };
    tick();
  });
}

/**
 * Voice paging for the waiting-area display (TV speakers).
 * Pass handleCallUpdate from the display status poll — one request updates UI + voice.
 */
export function useVoicePaging({
  storageKey = 'qms_display_audio_unlocked',
  enabled = true,
}) {
  const [audioUnlocked, setAudioUnlocked] = useState(
    () => safeStorageGet(storageKey) === '1'
  );
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const synthRef = useRef(getSpeechSynth());
  const audioCtxRef = useRef(null);
  const announcedKeysRef = useRef(new Set());
  const processingRef = useRef(false);

  const unlockAudio = useCallback(() => {
    safeStorageSet(storageKey, '1');
    setAudioUnlocked(true);
  }, [storageKey]);

  useEffect(() => {
    if (!enabled || audioUnlocked) return undefined;
    const onInteract = (e) => {
      if (e?.type === 'keydown' && !isActivateKey(e)) return;
      unlockAudio();
      window.removeEventListener('pointerdown', onInteract);
      window.removeEventListener('keydown', onInteract);
    };
    window.addEventListener('pointerdown', onInteract);
    window.addEventListener('keydown', onInteract);
    return () => {
      window.removeEventListener('pointerdown', onInteract);
      window.removeEventListener('keydown', onInteract);
    };
  }, [enabled, audioUnlocked, unlockAudio]);

  const getAudioCtx = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = createAudioContext();
    }
    return audioCtxRef.current;
  };

  useEffect(() => {
    if (!audioUnlocked) return;
    try {
      const ctx = getAudioCtx();
      ctx?.resume?.();
      const synth = synthRef.current;
      if (synth?.resume) synth.resume();
    } catch (e) {
      console.warn('[VOICE] Audio unlock failed:', e);
    }
  }, [audioUnlocked]);

  const playChime = useCallback(() => new Promise(resolve => {
    try {
      const ctx = getAudioCtx();
      if (!ctx) return resolve();
      ctx.resume?.();

      const playTone = (freq, startTime, duration) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, startTime);
        gain.gain.setValueAtTime(0.65, startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(startTime);
        osc.stop(startTime + duration);
      };

      playTone(523, ctx.currentTime, 0.25);
      playTone(659, ctx.currentTime + 0.28, 0.25);
      playTone(784, ctx.currentTime + 0.56, 0.35);
      setTimeout(resolve, 950);
    } catch (e) {
      console.warn('[VOICE] Chime failed:', e);
      resolve();
    }
  }), []);

  const speak = useCallback(async (call) => {
    const synth = synthRef.current;
    if (!synth || !audioUnlocked || typeof SpeechSynthesisUtterance === 'undefined') return;

    const ticketSpoken = (call.ticket_number || '').replace(/-/g, ' ').split('').join(' ').trim();
    const counter = counterSpoken(call);
    const text = `Ticket number ${ticketSpoken}. Please proceed to ${counter}.`;

    const ctx = getAudioCtx();
    try {
      await ctx?.suspend?.();
    } catch (e) {
      console.warn('[VOICE] AudioContext suspend failed:', e);
    }

    await delay(400);

    const voices = await waitForVoices(synth);
    const voice = pickVoice(voices);

    await new Promise(resolve => {
      let settled = false;
      let started = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      const utter = new SpeechSynthesisUtterance(text);
      utter.rate = 0.9;
      utter.pitch = 1;
      utter.volume = 1;
      utter.lang = 'en-GB';
      if (voice) utter.voice = voice;
      utter.onstart = () => { started = true; };
      utter.onend = done;
      utter.onerror = (e) => {
        console.warn('[VOICE] Speech error:', e);
        done();
      };

      try {
        if (synth.speaking || synth.pending) synth.cancel();
        if (typeof synth.resume === 'function') synth.resume();
        synth.speak(utter);
      } catch (e) {
        console.warn('[VOICE] speak() failed:', e);
        done();
        return;
      }

      // Single retry if TV drops the utterance — no repeat loop (avoids stutter)
      setTimeout(() => {
        if (settled || started) return;
        if (typeof synth.resume === 'function') synth.resume();
        if (!synth.speaking && !synth.pending) synth.speak(utter);
      }, 700);

      setTimeout(done, 20000);
    });

    try {
      await ctx?.resume?.();
    } catch (e) {
      console.warn('[VOICE] AudioContext resume failed:', e);
    }
  }, [audioUnlocked]);

  const handleCallUpdate = useCallback((recentCalls) => {
    if (!enabled || !audioUnlocked) return;
    const calls = Array.isArray(recentCalls) ? recentCalls : (recentCalls ? [recentCalls] : []);
    if (calls.length === 0) return;

    const newCalls = calls.filter((call) => {
      const key = callKey(call);
      return key && !announcedKeysRef.current.has(key);
    });
    if (newCalls.length === 0) return;

    newCalls.forEach((call) => announcedKeysRef.current.add(callKey(call)));
    setQueue(prev => [...prev, ...newCalls]);
  }, [enabled, audioUnlocked]);

  useEffect(() => {
    if (!enabled || !audioUnlocked || busy || queue.length === 0 || processingRef.current) return;

    const processQueue = async () => {
      processingRef.current = true;
      setBusy(true);
      const nextCall = queue[0];
      try {
        await playChime();
        await speak(nextCall);
      } finally {
        setQueue(prev => prev.slice(1));
        setBusy(false);
        processingRef.current = false;
      }
    };
    processQueue();
  }, [queue, busy, audioUnlocked, enabled, playChime, speak]);

  return { audioUnlocked, unlockAudio, handleCallUpdate };
}

export { isActivateKey };
